export const PROPOSAL_PREPARATION_STATES = Object.freeze({
  BLOCKED: "BLOCKED",
  NEEDS_MANAGEMENT_INPUT: "NEEDS_MANAGEMENT_INPUT",
  READY_TO_FORMALIZE: "READY_TO_FORMALIZE",
});

export const PROPOSAL_PREPARATION_CATEGORIES = Object.freeze([
  "Strategic Context",
  "Recommended Strategy",
  "Capital Allocation",
  "Expected Outcome",
  "Supporting Evidence",
  "Management Confirmation",
]);

const present = (value) => value !== null && value !== undefined && value !== "";
const finite = (value) => present(value) && Number.isFinite(Number(value));

const category = (name, blockers = [], warnings = [], evidence = []) => ({
  name,
  status: blockers.length ? "BLOCKED" : warnings.length ? "WARNING" : "COMPLETE",
  blockers,
  warnings,
  evidence,
});

function resolveAlternatives(recommendation) {
  return Array.isArray(recommendation?.alternatives)
    ? recommendation.alternatives.filter((alternative) => present(alternative?.id))
    : [];
}

/**
 * Classifies existing Decision Center evidence. It deliberately performs no
 * optimization, ranking, allocation, or outcome calculation.
 */
export function evaluateProposalPreparation(decisionCenterContext, management = {}) {
  const context = decisionCenterContext || {};
  const executiveDecision = context.executiveDecision;
  const recommendation = executiveDecision?.recommendation;
  const overall = context.overallPosition || {};
  const alternatives = resolveAlternatives(recommendation);

  const strategicBlockers = [];
  const strategicWarnings = [];
  const businessObjective = recommendation?.businessQuestion ||
    recommendation?.decisionRequested?.objective ||
    context.recommendations?.find(({ businessQuestion }) => present(businessQuestion))?.businessQuestion;
  const hasExposure = finite(overall.expectedAnnualLoss) || finite(overall.capitalAtRiskP95);
  const investmentDecision = recommendation?.decisionRequested;
  if (!present(businessObjective)) strategicWarnings.push("Business objective is not explicitly stated in the available evidence.");
  if (!hasExposure) strategicBlockers.push("Authoritative digital risk exposure is unavailable.");
  if (!investmentDecision) strategicBlockers.push("The investment decision to be prepared is unavailable.");

  const strategyBlockers = [];
  if (executiveDecision?.status !== "AVAILABLE" || !recommendation) {
    strategyBlockers.push("No authoritative strategy recommendation is available for Management review.");
  }

  const allocation = recommendation?.decisionRequested?.proposedCapitalAllocation;
  const allocationBlockers = finite(allocation)
    ? []
    : ["No authoritative proposed capital allocation is available."];

  const suppliedOutcome = recommendation?.expectedOutcome;
  const outcomeEvidence = (suppliedOutcome && (
    typeof suppliedOutcome !== "object" ||
    Object.keys(suppliedOutcome?.authoritativeMetrics || suppliedOutcome).length > 0
  ) ? suppliedOutcome : null) ||
    recommendation?.businessImpact ||
    recommendation?.impact ||
    alternatives.find(({ recommendationStatus }) => recommendationStatus === "RECOMMENDED")?.businessConsequence ||
    context.recommendations?.find(({ expectedImpact }) => present(expectedImpact))?.expectedImpact;
  const outcomeBlockers = outcomeEvidence
    ? []
    : ["The expected outcome is not available from the existing analytical evidence."];

  const evidenceBlockers = [];
  const evidenceWarnings = [];
  if (!context.engines?.portfolio?.data) evidenceBlockers.push("Portfolio analysis evidence is unavailable.");
  if (!context.engines?.scenario?.data) evidenceWarnings.push("Scenario analysis has not been added as supporting evidence.");
  if (!context.engines?.financing?.data) evidenceWarnings.push("Risk financing analysis has not been added as supporting evidence.");
  const governance = context.engines?.governance;
  if (governance?.status === "unavailable" && governance.reason === "FORBIDDEN") {
    evidenceWarnings.push("Governance evidence is unavailable because access is not permitted.");
  } else if (governance?.status === "unavailable" && governance.reason === "UNSUPPORTED_SCHEMA") {
    evidenceWarnings.push("Governance evidence is unavailable because its schema is unsupported.");
  } else if (governance?.status === "error") {
    evidenceWarnings.push("Governance evidence is currently unavailable because the request failed.");
  } else if (governance?.status !== "ready" && governance?.status !== "loading") {
    evidenceWarnings.push("Governance evidence is not currently available.");
  }
  if (!recommendation?.provenance && !context.engines?.portfolio?.data?.provenance) {
    evidenceWarnings.push("Evidence provenance is incomplete.");
  }

  const managementBlockers = [];
  const managementWarnings = [];
  const selectedAlternative = alternatives.find(({ id }) => String(id) === String(management.alternativeId));
  if (!management.position) managementWarnings.push("Management must choose whether to support the recommendation or select an evaluated alternative.");
  if (management.position === "ALTERNATIVE" && !selectedAlternative) {
    managementWarnings.push("Management must select an evaluated alternative.");
  }
  if (management.position === "ALTERNATIVE" && !String(management.justification || "").trim()) {
    managementWarnings.push("Management justification is required when selecting an alternative.");
  }
  if (!management.strategicContextConfirmed) managementWarnings.push("Management must confirm the strategic context.");
  if (!management.evidenceReviewed) managementWarnings.push("Management must confirm that the supporting evidence has been reviewed.");

  const categories = [
    category("Strategic Context", strategicBlockers, strategicWarnings, [businessObjective, hasExposure && "Digital risk exposure", investmentDecision && "Investment decision"].filter(Boolean)),
    category("Recommended Strategy", strategyBlockers, [], recommendation ? [recommendation.headline || recommendation.strategy || "Authoritative recommendation"] : []),
    category("Capital Allocation", allocationBlockers, [], finite(allocation) ? [allocation] : []),
    category("Expected Outcome", outcomeBlockers, [], outcomeEvidence ? [outcomeEvidence] : []),
    category("Supporting Evidence", evidenceBlockers, evidenceWarnings, Object.values(context.engines || {}).filter(({ data }) => data).map(({ data }) => data?.id || "Available evidence")),
    category("Management Confirmation", managementBlockers, managementWarnings, management.position ? [management.position, selectedAlternative?.id].filter(Boolean) : []),
  ];
  const blockers = categories.flatMap((item) => item.blockers.map((message) => ({ category: item.name, message })));
  const warnings = categories.flatMap((item) => item.warnings.map((message) => ({ category: item.name, message })));
  const managementComplete = managementWarnings.length === 0;

  return {
    state: blockers.length
      ? PROPOSAL_PREPARATION_STATES.BLOCKED
      : managementComplete
        ? PROPOSAL_PREPARATION_STATES.READY_TO_FORMALIZE
        : PROPOSAL_PREPARATION_STATES.NEEDS_MANAGEMENT_INPUT,
    categories,
    blockers,
    warnings,
    alternatives,
    managementSelection: management.position === "ALTERNATIVE" ? selectedAlternative || null : recommendation || null,
  };
}
